// Library Methods
import { useMemo } from "react";

// Components
import Snackbar, {
  SnackbarCloseReason,
  SnackbarOrigin,
} from "@mui/material/Snackbar";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { ErrorIcon, SuccessIcon } from "src/assets/icons";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

export type SnackbarInfoType = {
  variant: "error" | "success";
  message: string;
};

type SnackbarMessageType = {
  snackbarInfo: SnackbarInfoType | undefined;
  setSnackbarInfo: React.Dispatch<
    React.SetStateAction<SnackbarInfoType | undefined>
  >;
  autoHideDuration?: number;
  position?: SnackbarOrigin;
};

const SnackbarMessage = ({
  snackbarInfo,
  setSnackbarInfo,
  autoHideDuration = 3000,
  position = { vertical: "bottom", horizontal: "right" },
}: SnackbarMessageType) => {
  const theme = useTheme();
  const { surface, onSurface } = getSurfaceTokens(theme);

  const variant = useMemo(() => snackbarInfo?.variant, [snackbarInfo]);
  const message = useMemo(() => snackbarInfo?.message, [snackbarInfo]);

  const handleClose = (
    _?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarInfo(undefined);
  };

  const variantColor = useMemo(() => {
    switch (variant) {
      case "success":
        return "#22BE39";
      default:
        return "#E10C0C";
    }
  }, [variant]);

  const variantIcon = useMemo(() => {
    switch (variant) {
      case "success":
        return <SuccessIcon />;
      default:
        return <ErrorIcon />;
    }
  }, [variant]);

  if (!snackbarInfo) return <></>;

  return (
    <Snackbar
      open={Boolean(snackbarInfo)}
      onClose={handleClose}
      autoHideDuration={autoHideDuration}
      anchorOrigin={position}
      sx={{
        width: "fit-content",
        opacity: 1,
        backgroundColor: surface,
        right: { xs: "8px !important", sm: "24px !important" },
        left: { xs: "auto !important" },
      }}
      message={
        <Box display="flex" alignItems="center" gap="16px">
          <Box display="flex" alignItems="center" color={variantColor}>
            {variantIcon}
          </Box>
          <Box display="flex" alignItems="center">
            {message}
          </Box>
        </Box>
      }
      ContentProps={{
        sx: {
          border: `1px solid ${variantColor}`,
          borderRadius: "4px",
          color: onSurface,
          bgcolor: variantColor + "40",
          boxShadow: "none",
        },
      }}
    />
  );
};

export default SnackbarMessage;
